import React from "react";
import { effectGradient, effectStops } from "../lib/effectPresets";

function ArcImage({ src, width, curve, style, tintStops, tintOpacity = 1 }) {
  const canvasRef = React.useRef(null);
  const [img, setImg] = React.useState(null);

  React.useEffect(() => {
    if (!src) return;
    const im = new Image();
    im.onload = () => setImg(im);
    im.src = src;
  }, [src]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    const w = img.naturalWidth, h = img.naturalHeight;
    const cv = Number(curve) || 0;
    const amp = Math.round((Math.abs(cv) / 100) * w * 0.20);
    const outH = h + amp;
    canvas.width = w; canvas.height = outH;
    const ctx = canvas.getContext("2d");
    const cx = (w - 1) / 2;

    const draw = (time) => {
      ctx.clearRect(0, 0, w, outH);
      if (amp === 0) {
        ctx.drawImage(img, 0, 0);
      } else {
        for (let x = 0; x < w; x++) {
          const t = (x - cx) / cx;
          const delta = amp * (1 - t * t);
          const dy = cv >= 0 ? (amp - delta) : delta;
          ctx.drawImage(img, x, 0, 1, h, x, dy, 1, h);
        }
      }
      if (tintStops && tintStops.length) {
        const off = (((time || 0) / 6000) % 1) * w;
        const g = ctx.createLinearGradient(-w + off, 0, 2 * w + off, 0);
        const n = tintStops.length;
        tintStops.forEach((col, i) => g.addColorStop(i / (n - 1), col));
        ctx.globalCompositeOperation = "source-atop";
        ctx.globalAlpha = tintOpacity;
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, outH);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = "source-over";
      }
    };

    if (tintStops && tintStops.length) {
      let raf;
      const loop = (ts) => { draw(ts); raf = requestAnimationFrame(loop); };
      raf = requestAnimationFrame(loop);
      return () => cancelAnimationFrame(raf);
    }
    draw(0);
  }, [img, curve, width, tintStops, tintOpacity]);

  return <canvas ref={canvasRef} style={{ width: `${width}px`, height: "auto", display: "block", ...style }} data-testid="hero-logo-arc" />;
}

export const HERO_LOGO_DEFAULTS = {
  enabled: false,
  // Logo
  logo_visible: true,
  logo_image: "",
  logo_image_en: "",
  logo_width: 440,
  logo_effect: "none",
  logo_anim: "heroDown",
  logo_delay: 0,
  logo_duration: 0.9,
  logo_offset_x: 0,
  logo_offset_y: 0,
  logo_glow_enabled: false,
  logo_glow_color: "#000000",
  logo_glow_blur: 18,
  logo_glow_opacity: 0.45,
  logo_mold: "none",
  logo_mold_intensity: 50,
  logo_arc_curve: 0,
  logo_fx_theme: "none",
  logo_fx_paint: true,
  logo_fx_aura: true,
  logo_fx_opacity: 100,
  // Badge
  badge_enabled: true,
  badge_image: "",
  badge_image_en: "",
  badge_pt: "APRENDE · JOGA · SUPERA!",
  badge_en: "LEARN · PLAY · WIN!",
  badge_bg: "#C1121F",
  badge_color: "#FFFFFF",
  badge_border_color: "#FFD93D",
  badge_border_width: 2,
  badge_font: "luckiest",
  badge_size: 20,
  badge_radius: 999,
  badge_tilt: 0,
  badge_effect: "none",
  badge_anim: "heroUp",
  badge_delay: 0.35,
  badge_duration: 0.9,
  badge_width: 0,
  badge_offset_x: 0,
  badge_offset_y: 0,
  // Star (independent decorative element)
  star_enabled: false,
  star_image: "",
  star_width: 140,
  star_offset_x: 360,
  star_offset_y: -10,
  star_tilt: 0,
  star_effect: "float",
  star_anim: "heroPop",
  star_delay: 0.25,
  star_duration: 0.9,
};

const hexToRgba = (hex, a) => {
  const h = (hex || "#000000").replace("#", "");
  const f = h.length === 3 ? h.split("").map((x) => x + x).join("") : h;
  const n = parseInt(f, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

const ANIMS = {
  none: null, heroDown: "heroDown", heroUp: "heroUp", heroZoom: "heroZoom", heroPop: "heroPop", heroFade: "heroFade",
};

export const MOLD_OPTIONS = [
  ["none", "Nenhum"], ["arch", "Arco"], ["arch_strong", "Arco acentuado"], ["arch_inv", "Arco invertido"],
  ["convex", "Convexo"], ["dome", "Cúpula"], ["floor", "Perspetiva (chão)"], ["ceiling", "Perspetiva (teto)"],
  ["tilt_left", "Inclinar esquerda"], ["tilt_right", "Inclinar direita"], ["skew", "Itálico"], ["diagonal", "Diagonal"],
  ["squeeze", "Espremido"], ["stretch", "Esticado"], ["spin3d", "Rodar 3D"],
  ["wave", "Onda"], ["flag", "Bandeira"], ["ripple", "Ondulação"], ["bubble", "Bolha"],
];

const SVG_MOLDS = {
  wave: { bf: "0.012 0.02", oct: 2, k: 0.45, seed: 7 },
  flag: { bf: "0.008 0.002", oct: 2, k: 0.55, seed: 3 },
  ripple: { bf: "0.03 0.03", oct: 2, k: 0.30, seed: 11 },
  bubble: { bf: "0.02 0.02", oct: 1, k: 0.60, seed: 5 },
};

const MOLD = {
  none: () => ({}),
  arch: (i) => ({ transform: `perspective(700px) rotateX(${i * 0.35}deg)`, transformOrigin: "center bottom" }),
  arch_strong: (i) => ({ transform: `perspective(600px) rotateX(${i * 0.6}deg)`, transformOrigin: "center bottom" }),
  arch_inv: (i) => ({ transform: `perspective(700px) rotateX(${-i * 0.35}deg)`, transformOrigin: "center top" }),
  convex: (i) => ({ transform: `perspective(600px) rotateX(${i * 0.18}deg) scaleY(${1 + i * 0.0015})` }),
  dome: (i) => ({ transform: `perspective(500px) rotateX(${i * 0.3}deg) scale(${1 + i * 0.001})`, transformOrigin: "center top" }),
  floor: (i) => ({ transform: `perspective(400px) rotateX(${i * 0.55}deg)`, transformOrigin: "center bottom" }),
  ceiling: (i) => ({ transform: `perspective(400px) rotateX(${-i * 0.55}deg)`, transformOrigin: "center top" }),
  tilt_left: (i) => ({ transform: `perspective(900px) rotateY(${-i * 0.4}deg)`, transformOrigin: "left center" }),
  tilt_right: (i) => ({ transform: `perspective(900px) rotateY(${i * 0.4}deg)`, transformOrigin: "right center" }),
  skew: (i) => ({ transform: `skewX(${-i * 0.18}deg)` }),
  diagonal: (i) => ({ transform: `skewY(${i * 0.12}deg)` }),
  squeeze: (i) => ({ transform: `scaleX(${Math.max(0.4, 1 - i * 0.004)})` }),
  stretch: (i) => ({ transform: `scaleY(${1 + i * 0.006})` }),
  spin3d: (i) => ({ transform: `perspective(900px) rotateY(${i * 0.55}deg)` }),
  wave: () => ({ filter: "url(#hero-mold-wave)" }),
  flag: () => ({ filter: "url(#hero-mold-flag)" }),
  ripple: () => ({ filter: "url(#hero-mold-ripple)" }),
  bubble: () => ({ filter: "url(#hero-mold-bubble)" }),
};

export default function HeroLogo({ config, lang = "pt", backendUrl, preview = false }) {
  const base = { ...HERO_LOGO_DEFAULTS, ...(config || {}) };
  // EN can fully override geometry/style/effects via base.en (independent per-language settings).
  const c = lang === "en" && base.en ? { ...base, ...base.en } : base;

  // Responsive fit: scale the whole composed logo (logo + badge + star + px offsets)
  // down to fit the available width, so it never overflows on small/landscape mobile.
  const outerRef = React.useRef(null);
  const stageRef = React.useRef(null);
  const [fit, setFit] = React.useState({ scale: 1, h: undefined });
  React.useEffect(() => {
    const el = outerRef.current, st = stageRef.current;
    if (!el || !st) return;
    const recalc = () => {
      const avail = el.clientWidth || 1;
      const natural = st.offsetWidth || 1;
      const s = Math.min(1, avail / natural);
      setFit({ scale: s, h: s < 1 ? Math.ceil(st.offsetHeight * s) : undefined });
    };
    recalc();
    const ro = new ResizeObserver(recalc);
    ro.observe(el); ro.observe(st);
    window.addEventListener("resize", recalc);
    return () => { ro.disconnect(); window.removeEventListener("resize", recalc); };
  }, []);

  if (!preview && c.enabled === false) return null;

  const fileUrl = (p) => (p ? `${backendUrl}/api/files/${p}` : null);
  // Dedicated per-language asset/text keys are stored at top level (base), never inside base.en.
  const logoPath = lang === "en" && base.logo_image_en ? base.logo_image_en : base.logo_image;
  const logoUrl = fileUrl(logoPath);
  const badgeImgPath = lang === "en" && base.badge_image_en ? base.badge_image_en : base.badge_image;
  const badgeImgUrl = fileUrl(badgeImgPath);
  const badgeText = lang === "en" ? (base.badge_en || base.badge_pt) : base.badge_pt;
  const starUrl = fileUrl(lang === "en" && base.star_image_en ? base.star_image_en : base.star_image);

  const animStyle = (name, dur, delay) => {
    const a = ANIMS[name];
    return a ? { animation: `${a} ${Math.max(0.1, Number(dur) || 0.9)}s ease both`, animationDelay: `${Number(delay) || 0}s` } : {};
  };
  const fx = (e) => (e && e !== "none" ? `promo-card-fx-${e}` : "");
  const translate = (x, y) => `translate(${Number(x) || 0}px, ${Number(y) || 0}px)`;

  const showLogo = c.logo_visible !== false && !!logoUrl;
  const showBadge = c.badge_enabled !== false && (badgeImgUrl || badgeText);
  const showStar = c.star_enabled === true && !!starUrl;

  return (
    <div ref={outerRef} style={{ width: "100%", display: "flex", justifyContent: "center", height: fit.h ? `${fit.h}px` : undefined }}>
      <div ref={stageRef} style={{ transform: fit.scale < 1 ? `scale(${fit.scale})` : undefined, transformOrigin: "top center" }}>
        <div className="relative flex flex-col items-center gap-4" data-testid="hero-logo">
      {showLogo && (
        <div className={fx(c.logo_effect)} style={{ transform: translate(c.logo_offset_x, c.logo_offset_y) }} data-testid="hero-logo-wrap">
          {SVG_MOLDS[c.logo_mold] && (() => {
            const s = SVG_MOLDS[c.logo_mold];
            const i = Number(c.logo_mold_intensity) || 50;
            return (
              <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden>
                <filter id={`hero-mold-${c.logo_mold}`}>
                  <feTurbulence type="fractalNoise" baseFrequency={s.bf} numOctaves={s.oct} seed={s.seed} result="noise" />
                  <feDisplacementMap in="SourceGraphic" in2="noise" scale={Math.max(2, i * s.k)} xChannelSelector="R" yChannelSelector="G" />
                </filter>
              </svg>
            );
          })()}
          {(() => {
            const moldFn = MOLD[c.logo_mold] || MOLD.none;
            const mold = moldFn(Number(c.logo_mold_intensity) || 50);
            const glowFilter = c.logo_glow_enabled ? `drop-shadow(0 0 ${Number(c.logo_glow_blur) || 0}px ${hexToRgba(c.logo_glow_color, Number(c.logo_glow_opacity))})` : "";
            const filterStr = [glowFilter, mold.filter].filter(Boolean).join(" ") || undefined;
            const stops = effectStops(c.logo_fx_theme);
            const fxOpacity = Math.max(0, Math.min(100, Number(c.logo_fx_opacity ?? 100))) / 100;
            const moldStyle = { transform: mold.transform, transformOrigin: mold.transformOrigin };
            const mainFilter = [glowFilter, mold.filter].filter(Boolean).join(" ") || undefined;
            if (stops) {
              // Effect is baked into the warped canvas so it follows mold + arc exactly.
              return (
                <div style={animStyle(c.logo_anim, c.logo_duration, c.logo_delay)} data-testid="hero-logo-img">
                  <span style={{ position: "relative", display: "inline-block", lineHeight: 0 }} data-testid="hero-logo-fx">
                    {c.logo_fx_aura !== false && (
                      <span aria-hidden style={{ position: "absolute", inset: 0, zIndex: 0, filter: "blur(16px)", transform: "scale(1.1)", opacity: 0.85, pointerEvents: "none" }}>
                        <ArcImage src={logoUrl} width={c.logo_width} curve={c.logo_arc_curve} tintStops={stops} tintOpacity={1} style={moldStyle} />
                      </span>
                    )}
                    <span style={{ position: "relative", zIndex: 1, display: "inline-block" }}>
                      <ArcImage src={logoUrl} width={c.logo_width} curve={c.logo_arc_curve} tintStops={c.logo_fx_paint !== false ? stops : null} tintOpacity={fxOpacity} style={{ ...moldStyle, filter: mainFilter }} />
                    </span>
                  </span>
                </div>
              );
            }
            return (
              <div style={animStyle(c.logo_anim, c.logo_duration, c.logo_delay)} data-testid="hero-logo-img">
                {Number(c.logo_arc_curve) ? (
                  <ArcImage src={logoUrl} width={c.logo_width} curve={c.logo_arc_curve} style={{ filter: filterStr, ...moldStyle }} />
                ) : (
                  <img src={logoUrl} alt="logo" style={{ width: `${c.logo_width}px`, height: "auto", filter: filterStr, ...moldStyle }} />
                )}
              </div>
            );
          })()}
        </div>
      )}

      {showBadge && (
        <div className={fx(c.badge_effect)} style={{ transform: translate(c.badge_offset_x, c.badge_offset_y) }} data-testid="hero-badge">
          <div style={animStyle(c.badge_anim, c.badge_duration, c.badge_delay)}>
            {badgeImgUrl ? (
              <img src={badgeImgUrl} alt="badge" style={{ width: c.badge_width ? `${c.badge_width}px` : undefined, height: "auto" }} />
            ) : (
              <span className={`inline-block font-${c.badge_font || "luckiest"} font-extrabold`}
                style={{
                  background: c.badge_bg, color: c.badge_color,
                  border: (Number(c.badge_border_width) || 0) > 0 ? `${c.badge_border_width}px solid ${c.badge_border_color}` : "none",
                  borderRadius: `${c.badge_radius}px`, padding: "8px 22px", fontSize: `${c.badge_size}px`,
                  transform: c.badge_tilt ? `rotate(${c.badge_tilt}deg)` : undefined,
                  boxShadow: "0 8px 20px rgba(0,0,0,0.28)", textShadow: "1px 1px 0 rgba(0,0,0,0.18)",
                  letterSpacing: "0.5px",
                }}>
                {badgeText}
              </span>
            )}
          </div>
        </div>
      )}

      {showStar && (
        <div className={fx(c.star_effect)} data-testid="hero-star"
          style={{
            position: "absolute", top: 0, left: "50%",
            transform: `translate(calc(-50% + ${Number(c.star_offset_x) || 0}px), calc(-50% + ${Number(c.star_offset_y) || 0}px))`,
            pointerEvents: "none", zIndex: 5,
          }}>
          <div style={animStyle(c.star_anim, c.star_duration, c.star_delay)}>
            <img src={starUrl} alt="star" style={{
              width: `${c.star_width}px`, height: "auto",
              transform: c.star_tilt ? `rotate(${c.star_tilt}deg)` : undefined,
            }} />
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}
